import { PublicClientApplication, type Configuration, type AuthenticationResult } from '@azure/msal-browser'

const clientId = import.meta.env.VITE_MS_SSO_CLIENT_ID ?? ''
const tenantId = import.meta.env.VITE_MS_SSO_TENANT_ID ?? ''

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: `${window.location.origin}/login`
  },
  cache: {
    cacheLocation: 'sessionStorage'
  }
}

export const loginRequest = {
  scopes: ['openid', 'profile', 'email']
}

export const msalInstance = new PublicClientApplication(msalConfig)

export const isSsoEnabled = Boolean(clientId && tenantId)

export const msalReady = isSsoEnabled ? msalInstance.initialize() : Promise.resolve()

let redirectPromise: Promise<AuthenticationResult | null> | null = null

/**
 * Singleton wrapper — ensures handleRedirectPromise() is called at most once
 * per page load, preventing duplicate processing of the redirect response.
 */
export function handleRedirectOnce(): Promise<AuthenticationResult | null> {
  if (!redirectPromise) {
    redirectPromise = msalInstance.handleRedirectPromise()
  }
  return redirectPromise
}

/**
 * Clear stale MSAL interaction state that blocks loginRedirect.
 * When a redirect is interrupted (network, browser back, etc.), MSAL leaves
 * interaction markers in sessionStorage that persist across page refreshes.
 *
 * Purging all msal.* sessionStorage keys is safe on the login page since no
 * authenticated session exists yet. initialize() is called afterwards — MSAL v3
 * short-circuits if already initialized, but loginRedirect's internal check will
 * re-read sessionStorage and see the markers are gone, allowing a new interaction.
 */
export async function clearStaleInteraction(): Promise<void> {
  const keysToRemove = Object.keys(sessionStorage).filter(k => k.startsWith('msal.'))
  keysToRemove.forEach(k => sessionStorage.removeItem(k))
  await msalInstance.initialize()
}
